import axios from "axios";
import { toast } from "react-toastify";

const instance = axios.create({
  baseURL: "http://localhost:5000",
  timeout: 10000,
  params: {},
});

instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = "Bearer " + token;
    }
    config.baseURL = process.env.REACT_APP_BTC_API_URL;
    config.headers["Content-Type"] = "application/json";
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    return response.data.data;
  },
  function (error) {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest.url === process.env.REACT_APP_BTC_API_URL
    ) {
      window.location.replace("/");
      return Promise.reject(error);
    }

    if (error.response?.status === 400) {
      return Promise.reject(error.response.data.message);
    }

    if (error.response?.status === 500) {
      return Promise.reject(error.response.data.message);
    }
    return Promise.reject(error);
  }
);

export const LoginUser = async (body) => {
  try {
    const res = await instance.post("/api/auth/login_web", body);
    toast.info("Logged in successfully");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const SignUpUser = async (body) => {
  try {
    const res = await instance.post("/api/auth/sign-up", body);
    toast.info("Signed up successfully");
    return res;
  } catch (error) {
    throw error;
  }
};

export const getUserProfile = async () => {
  try {
    return await instance.get(`/api/users`);
  } catch (error) {
    throw error;
  }
};

export const getUserStats = async () => {
  try {
    return await instance.get(`api/users/stats`);
  } catch (error) {
    throw error;
  }
};

export const getVehicles = async (
  tag,
  userId,
  searchText,
  country,
  maker,
  page,
  limit
) => {
  try {
    let fields = "";

    if (page) {
      fields += "page=" + page + "&";
    }

    if (limit) {
      fields += "limit=" + limit + "&";
    }

    if (searchText) {
      fields += "searchText=" + searchText + "&";
    }

    if (tag) {
      fields += "tag=" + tag + "&";
    }

    if (userId) {
      fields += "userId=" + userId + "&";
    }
    if (country !== undefined) {
      fields += "&country=" + country;
    }

    if (maker !== undefined) {
      fields += "maker=" + maker;
    }

    return await instance.get("/api/vehicles?" + fields);
  } catch (error) {
    toast.error(error);
  }
};

export const getCountries = async () => {
  try {
    return await instance.get(`/api/countries`);
  } catch (error) {
    toast.error(error);
  }
};

export const getCarMakers = async () => {
  try {
    return await instance.get(`/api/car-makers`);
  } catch (error) {
    toast.error(error);
  }
};

export const getVehicle = async (vehicleId, userId) => {
  try {
    return await instance.get(
      `/api/vehicles/details/${vehicleId}${userId ? `?userId=${userId}` : ""}`
    );
  } catch (error) {
    toast.error(error);
  }
};

export const getVehiclesByCategory = async (vehicleCategory) => {
  try {
    return await instance.get(`/api/vehicles/by-category/${vehicleCategory}`);
  } catch (error) {
    toast.error(error);
  }
};

export const AddVehicleToFavorites = async (userId, body) => {
  try {
    const res = await instance.post(`/api/favorites/${userId}`, body);

    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const addInquiry = async (body) => {
  try {
    const res = await instance.post("/api/inquiries", body);
    toast.info("Inquiry submitted successfully");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const bid = async (vehicleId, body) => {
  try {
    const res = await instance.post(`/api/bids/${vehicleId}`, body);
    toast.info("Bid submitted.");

    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const offer = async (vehicleId, body) => {
  try {
    const res = await instance.post(`/api/bids/tender/${vehicleId}`, body);
    toast.info("Offer sent successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const negotiate = async (vehicleId, body) => {
  try {
    const res = await instance.post(`/api/bids/negotiation/${vehicleId}`, body);
    toast.info("Offer sent successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const getFavourites = async (limit, offset, userId) => {
  try {
    return await instance.get(
      `/api/favorites/${userId}?page=${offset}&limit=${limit}`
    );
  } catch (error) {
    toast.error(error);
  }
};

export const getVehiclesByAuction = async (
  limit,
  offset,
  userId,
  auctionType,
  inProcess
) => {
  try {
    return await instance.get(
      `/api/vehicles/on-auction?userId=${userId}&auctionType=${auctionType}&inProcess=${inProcess}&page=${offset}&limit=${limit}`
    );
  } catch (error) {
    toast.error(error);
  }
};

export const getUserNotifications = async (limit, offset) => {
  try {
    return await instance.get(
      `api/users/user-notifications?page=${offset}&limit=${limit}`
    );
  } catch (error) {
    toast.error(error);
  }
};

export const addLogistics = async (body) => {
  try {
    const res = await instance.post(`/api/logistics`, body);
    toast.info("Query sent successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const addTransport = async (body) => {
  try {
    const res = await instance.post(`/api/transports`, body);
    toast.info("Query sent successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const getAdminDefaults = async () => {
  try {
    return await instance.get(`/admin/defaults`);
  } catch (error) {
    toast.error(error);
  }
};

export const searchVehicles = async (queryParamsObj) => {
  const queryParams = Object.entries(queryParamsObj)
    .filter(([key, value]) => value !== null)
    .filter(([key, value]) => value !== "Unspecified")
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`) // Encode parameter values
    .join("&");
  try {
    return await instance.get("/api/vehicles/search?" + queryParams);
  } catch (error) {
    toast.error(error);
  }
};

export const UpdateMyPage = async (body) => {
  try {
    const res = await instance.post(`/api/users/update_my_page`, body);
    toast.info("updated your page successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const SignUp = async (body) => {
  try {
    const res = await instance.post(`/api/auth/sign-up`, body);
    toast.info("sign up successfully.");
    window.location.replace('/login')
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const UpdatePassword = async (body) => {
  try {
    const res = await instance.post(`/api/users/update_password`, body);
    toast.info("changed password successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const CheckDuplicity = async (body) => {
  try {
    const res = await instance.post(`/api/auth/already-exists`, body);
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const Notification = async (body) => {
  try {
    const res = await instance.post(
      `/api/users/update_notification_settings`,
      body
    );
    toast.info("Notification Settings updated successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const uploadImage = async (image) => {
  try {
    const res = await instance.post("/", image, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    toast.info("Image updated successfully.");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const SendOtp = async (body) => {
  try {
    const res = await instance.post(`/api/auth/send_email_otp`, body).then(res => {
      toast.info("otp code send to your Gmail account");
      return res;
    }).catch(error => {
      throw error;
    });
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const VerifyOtp = async (body) => {
  try {
    const res = await instance.post(`/api/auth/verify_otp`, body);
    toast.info("your email is verified");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const SendPhoneOTP = async (body) => {
  try {
    const res = await instance.post(`/api/auth/send_whatsapp_otp`, body).then(res => {
      toast.info("otp send to your phone number");
      return res;
    }).catch(error => {
      throw error;
    })
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const VerifyPhoneNumberOtp = async (body) => {
  try {
    const res = await instance.post(`/api/auth/verify_phone_otp`, body);
    toast.info("phone number is verified");
    return res;
  } catch (error) {
    toast.error(error);
  }
};

export const getUniqueUsernameSrNo = async () => {
  try {
    return await instance.get(`/api/auth/get-sr-no`);
  } catch (error) {
    toast.error(error);
  }
};
